<script setup lang="ts">
import RichTextEditor from "@/components/text-editor/index.vue";
import { useProductNoun } from "@/composition/useProductNoun";
import {
  isValidRedirectUrl,
  type ScheduleAppearance,
  type VisibilityState,
  type VisibilityStateKey,
  type VisibilityStateMode,
} from "@/util/formSchedule";
import {
  ChevronDownIcon,
  ClockIcon,
  Lock01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIInput, UIRadio } from "@gohighlevel/ghl-ui";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import VisibilityPreview from "./VisibilityPreview.vue";

const { t } = useI18n();
const { productParams } = useProductNoun();

const props = defineProps<{
  states: Record<VisibilityStateKey, VisibilityState>;
  appearance: ScheduleAppearance;
}>();

const emit = defineEmits<{
  (e: "update:state", key: VisibilityStateKey, value: VisibilityState): void;
}>();

const activeKey = ref<VisibilityStateKey>("before");

const cards: { key: VisibilityStateKey }[] = [
  { key: "before" },
  { key: "after" },
];

const iconFor = (key: VisibilityStateKey) =>
  key === "before" ? ClockIcon : Lock01Icon;

// Static i18n keys (no dynamic-key lint violation).
const titleFor = (key: VisibilityStateKey): string =>
  key === "before"
    ? t("form.scheduleVisibility.states.beforeTitle", productParams.value)
    : t("form.scheduleVisibility.states.afterTitle", productParams.value);

const stripProtocol = (url: string): string =>
  url.replace(/^https?:\/\//i, "").split("/")[0] || url;

const summaryFor = (key: VisibilityStateKey): string => {
  const state = props.states[key];
  if (state.mode === "redirect" && state.redirectUrl) {
    return t("form.scheduleVisibility.states.redirectsTo", {
      url: stripProtocol(state.redirectUrl),
    });
  }
  return t("form.scheduleVisibility.states.showsPage");
};

const toggleCard = (key: VisibilityStateKey) => {
  activeKey.value = activeKey.value === key ? ("" as VisibilityStateKey) : key;
};

const setMode = (key: VisibilityStateKey, mode: VisibilityStateMode) => {
  emit("update:state", key, { ...props.states[key], mode });
};

const setHtml = (key: VisibilityStateKey, html: string) => {
  emit("update:state", key, { ...props.states[key], html });
};

/* Redirect-URL validation: flagged on blur (not mid-typing), then re-checked
   live so the error clears as soon as the user fixes the URL. */
const redirectUrlErrors = ref<Record<VisibilityStateKey, boolean>>({
  before: false,
  after: false,
});

const hasRedirectUrlError = (url: string): boolean =>
  url.trim() !== "" && !isValidRedirectUrl(url);

const validateRedirectUrl = (key: VisibilityStateKey) => {
  redirectUrlErrors.value = {
    ...redirectUrlErrors.value,
    [key]: hasRedirectUrlError(props.states[key].redirectUrl ?? ""),
  };
};

const setRedirectUrl = (key: VisibilityStateKey, redirectUrl: string) => {
  emit("update:state", key, { ...props.states[key], redirectUrl });
  if (redirectUrlErrors.value[key]) {
    redirectUrlErrors.value = {
      ...redirectUrlErrors.value,
      [key]: hasRedirectUrlError(redirectUrl),
    };
  }
};

const isActive = (key: VisibilityStateKey) => activeKey.value === key;
</script>

<template>
  <div class="state-cards">
    <p class="section-label">
      {{ t("form.scheduleVisibility.whatVisitorsSee") }}
    </p>
    <p class="section-help">
      {{ t("form.scheduleVisibility.whatVisitorsSeeHelp") }}
    </p>

    <div v-for="card in cards" :key="card.key" class="state-card">
      <button
        :id="`schedule-state-header-${card.key}`"
        type="button"
        class="state-card__header"
        :aria-expanded="isActive(card.key)"
        @click="toggleCard(card.key)"
      >
        <span
          class="state-card__icon"
          :class="{ 'state-card__icon--active': isActive(card.key) }"
        >
          <component :is="iconFor(card.key)" class="state-card__icon-svg" />
        </span>
        <span class="state-card__text">
          <span class="state-card__title">{{ titleFor(card.key) }}</span>
          <span class="state-card__summary">{{ summaryFor(card.key) }}</span>
        </span>
        <ChevronDownIcon
          class="h-4 w-4 flex-shrink-0 text-gray-400 transition-transform"
          :class="{ 'rotate-180': isActive(card.key) }"
        />
      </button>

      <div v-if="isActive(card.key)" class="state-card__body">
        <div class="mode-grid" role="radiogroup">
          <div
            :id="`schedule-mode-page-${card.key}`"
            class="mode-option"
            role="radio"
            tabindex="0"
            :aria-checked="states[card.key].mode === 'page'"
            :class="{
              'mode-option--selected': states[card.key].mode === 'page',
            }"
            @click="setMode(card.key, 'page')"
            @keydown.enter.prevent="setMode(card.key, 'page')"
            @keydown.space.prevent="setMode(card.key, 'page')"
          >
            <UIRadio
              :id="`schedule-mode-page-${card.key}-indicator`"
              class="mode-option__radio"
              :checked="states[card.key].mode === 'page'"
            />
            <div>
              <p class="mode-option__title">
                {{ t("form.scheduleVisibility.showPage") }}
              </p>
              <p class="mode-option__help">
                {{ t("form.scheduleVisibility.showPageHelp") }}
              </p>
            </div>
          </div>
          <div
            :id="`schedule-mode-redirect-${card.key}`"
            class="mode-option"
            role="radio"
            tabindex="0"
            :aria-checked="states[card.key].mode === 'redirect'"
            :class="{
              'mode-option--selected': states[card.key].mode === 'redirect',
            }"
            @click="setMode(card.key, 'redirect')"
            @keydown.enter.prevent="setMode(card.key, 'redirect')"
            @keydown.space.prevent="setMode(card.key, 'redirect')"
          >
            <UIRadio
              :id="`schedule-mode-redirect-${card.key}-indicator`"
              class="mode-option__radio"
              :checked="states[card.key].mode === 'redirect'"
            />
            <div>
              <p class="mode-option__title">
                {{ t("form.scheduleVisibility.openLink") }}
              </p>
              <p class="mode-option__help">
                {{ t("form.scheduleVisibility.openLinkHelp") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Show a page: rich-text editor doubles as the live preview -->
        <div v-if="states[card.key].mode === 'page'" class="mode-panel">
          <RichTextEditor
            :model-value="states[card.key].html"
            :is-submit-message="true"
            custom-width="100%"
            @update:model-value="setHtml(card.key, $event)"
          />
        </div>

        <!-- Open a link: redirect URL -->
        <div v-else class="mode-panel">
          <p class="field-label">
            {{ t("form.scheduleVisibility.redirectUrl") }}
          </p>
          <UIInput
            :id="`schedule-redirect-${card.key}`"
            :model-value="states[card.key].redirectUrl"
            :placeholder="t('form.scheduleVisibility.redirectUrlPlaceholder')"
            :status="redirectUrlErrors[card.key] ? 'error' : undefined"
            @update:model-value="setRedirectUrl(card.key, $event)"
            @blur="validateRedirectUrl(card.key)"
          />
          <p v-if="redirectUrlErrors[card.key]" class="field-error" role="alert">
            {{ t("form.scheduleVisibility.redirectUrlInvalid") }}
          </p>
          <p v-else class="field-help">
            {{ t("form.scheduleVisibility.redirectUrlHelp") }}
          </p>
        </div>

        <!-- Live preview of what visitors see -->
        <VisibilityPreview :state="states[card.key]" :appearance="appearance" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.state-cards {
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Section divider separating the schedule window (time zone) from the
     "What visitors see" section (matches design). */
  padding-top: 24px;
  border-top: 1px solid #eaecf0;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  /* Drop the browser default paragraph margin (Settings' scoped reset doesn't
     reach this child component); spacing comes from the flex gap / helpers. */
  margin: 0;
}

.section-help {
  font-size: 12px;
  color: #667085;
  margin: 0 0 8px;
}

.state-card {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  margin-bottom: 8px;
  overflow: hidden;
}

.state-card__header {
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  padding: 14px 16px;
  background-color: #ffffff;
  border: 0;
  cursor: pointer;
  text-align: left;
  outline: none;
}

.state-card__header:focus-visible {
  box-shadow: inset 0 0 0 2px #155eef;
}

/* Tinted square behind the state icon; goes accent-blue while expanded. */
.state-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f2f4f7;
  color: #667085;
  transition: background 120ms ease-out, color 120ms ease-out;
}

.state-card__icon--active {
  background: #eff4ff;
  color: #155eef;
}

.state-card__icon-svg {
  width: 18px;
  height: 18px;
}

.state-card__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.state-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}

.state-card__summary {
  font-size: 13px;
  color: #667085;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state-card__body {
  padding: 16px;
  border-top: 1px solid #eaecf0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mode-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mode-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
}

.mode-option:focus-visible {
  box-shadow: 0 0 0 2px #155eef;
}

/* The radio is a visual indicator only — the whole card handles the click. */
.mode-option__radio {
  pointer-events: none;
}

.mode-option--selected {
  border-color: #155eef;
  background-color: #eff4ff;
}

.mode-option__title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  /* Drop the browser's default paragraph margin (Settings' scoped reset
     doesn't reach this child component); keep a tight 2px gap to the help. */
  margin: 0 0 2px;
}

.mode-option__help {
  font-size: 12px;
  color: #667085;
  margin: 0;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}

.field-help {
  font-size: 12px;
  color: #667085;
  margin-top: 6px;
}

.field-error {
  font-size: 12px;
  color: #d92d20;
  margin-top: 6px;
}
</style>
